import { useState, useCallback, useMemo, useEffect } from 'react';
import type { ColumnFiltersState } from '@tanstack/react-table';

/**
 * Deep equality check for filter values
 * Handles arrays, objects, and primitive values
 */
function isFilterValueEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  
  // Handle arrays (e.g., string[] for select filters)
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    return a.every((item, index) => item === b[index]);
  }
  
  // Handle objects (e.g., { from?: Date; to?: Date } for date filters)
  if (typeof a === 'object' && typeof b === 'object') {
    const keysA = Object.keys(a as object);
    const keysB = Object.keys(b as object);
    
    if (keysA.length !== keysB.length) return false;
    
    return keysA.every(key => {
      const valueA = (a as Record<string, unknown>)[key];
      const valueB = (b as Record<string, unknown>)[key];
      
      // Handle Date objects specifically
      if (valueA instanceof Date && valueB instanceof Date) {
        return valueA.getTime() === valueB.getTime();
      }
      
      return valueA === valueB;
    });
  }
  
  return false;
}

/**
 * Check if a global filter string is meaningful (non-empty after trimming)
 */
function isGlobalFilterActive(filter: string): boolean {
  return filter.trim().length > 0;
}

// Custom debounce hook
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

interface UseTableFiltersOptions {
  /** Initial column filters to apply on mount */
  initialColumnFilters?: ColumnFiltersState;
}

/**
 * Custom hook for managing table search and filter state
 * Provides debounced search functionality and column filter management
 */
export function useTableFilters(options: UseTableFiltersOptions = {}) {
  const { initialColumnFilters } = options;
  const [globalFilterInput, setGlobalFilterInput] = useState<string>('');
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(
    () => initialColumnFilters || []
  );

  // Debounced global filter to prevent excessive re-renders during typing
  const debouncedGlobalFilter = useDebounce(globalFilterInput, 300);

  // Set filter for specific column
  const setColumnFilter = useCallback((columnId: string, value: unknown) => {
    setColumnFilters((prev) => {
      const existing = prev.find((filter) => filter.id === columnId);
      
      // Check for empty values (arrays, objects, null, undefined)
      const isEmpty = !value || 
        (Array.isArray(value) && value.length === 0) ||
        (typeof value === 'object' && Object.keys(value as object).length === 0);
      
      if (isEmpty) {
        // Remove filter if value is empty
        return prev.filter((filter) => filter.id !== columnId);
      }
      
      if (existing) {
        // Check if the new value is the same as existing to avoid unnecessary updates
        if (isFilterValueEqual(existing.value, value)) {
          return prev; // Return unchanged state to prevent rerenders
        }
        
        // Update existing filter with new value
        return prev.map((filter) =>
          filter.id === columnId ? { id: columnId, value } : filter
        );
      } else {
        // Add new filter
        return [...prev, { id: columnId, value }];
      }
    });
  }, []);

  // Clear all filters
  const resetFilters = useCallback(() => {
    setGlobalFilterInput('');
    setColumnFilters([]);
  }, []);

  // Clear specific column filter
  const clearColumnFilter = useCallback((columnId: string) => {
    setColumnFilters((prev) => prev.filter((filter) => filter.id !== columnId));
  }, []);

  // Get filter value for specific column
  const getColumnFilterValue = useCallback((columnId: string) => {
    return columnFilters.find((filter) => filter.id === columnId)?.value;
  }, [columnFilters]);

  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    return isGlobalFilterActive(debouncedGlobalFilter) || columnFilters.length > 0;
  }, [debouncedGlobalFilter, columnFilters]);

  // Count of active filters
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (isGlobalFilterActive(debouncedGlobalFilter)) count++;
    count += columnFilters.length;
    return count;
  }, [debouncedGlobalFilter, columnFilters]);

  // Handle column filters update from table
  const handleColumnFiltersChange = useCallback((filters: ColumnFiltersState) => {
    setColumnFilters(filters);
  }, []);

  return {
    // State
    globalFilter: globalFilterInput,
    columnFilters,
    hasActiveFilters,
    activeFilterCount,
    
    // Actions
    setGlobalFilter: setGlobalFilterInput,
    setColumnFilter,
    clearColumnFilter,
    resetFilters,
    getColumnFilterValue,
    handleColumnFiltersChange,
    
    // Internal state for table configuration
    _internalGlobalFilter: debouncedGlobalFilter,
    _internalColumnFilters: columnFilters,
  };
}